/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { LastModified } from '../../../../../../../../common/ui/last-modified';
import { RestrictedIcon } from '../../../../../../../../common/ui/restricted-icon';
import { SearchResult } from '../../../../../../../../common/ui/search-result';
import { isNonPublic } from '../../../../../../../../common/utils/is-non-public';
import { enrichExcerpt } from '../../../../../../../../common/utils/search-result-utils';
import { messages } from '../messages';

import type { VideoResultRendererProps } from './types';

export interface BaseVideoSearchResultProps extends VideoResultRendererProps {
	/** The body/description content to render */
	body: React.ReactNode;
	/** Optional footer component (e.g., chapters, linked results) */
	footer?: React.ReactNode;
	/** Whether this result has related links */
	hasRelatedLinks?: boolean;
	/** Additional metadata parts to include after the type label */
	additionalMetadataParts?: Array<string | [string, React.ReactNode | string | null]>;
	/** Optional custom title to override the data title (e.g., for meeting transcripts with date appended) */
	customTitle?: string;
}

/**
 * Base component for video-type search results (Loom videos, MS Teams transcripts, etc.).
 * Handles common rendering logic like creator name, last modified, permission icon.
 */
export const BaseVideoSearchResult: ({
	data,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	appMode,
	product,
	icon,
	mainContentWidth,
	body,
	footer,
	hasRelatedLinks,
	additionalMetadataParts,
	customTitle,
}: BaseVideoSearchResultProps) => JSX.Element = ({
	data,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	appMode,
	product,
	icon,
	mainContentWidth,
	// Customization props
	body,
	footer,
	hasRelatedLinks = false,
	additionalMetadataParts = [],
	customTitle,
}: BaseVideoSearchResultProps) => {
	const { formatMessage } = useIntl();
	const { lastModifiedDate, url, permissionLevel, entity, title } = data;
	const { createdBy } = entity || {};
	const creatorName = createdBy?.user?.name || createdBy?.thirdPartyUser?.name || null;

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	const headerMetadataParts = [
		formatMessage(messages.videoType),
		['creator-name', creatorName],
		['last-modified', lastModifiedComponent],
		...additionalMetadataParts,
		['padlock', padlockIcon],
	];

	return (
		<SearchResult
			id={data.id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			description={body}
			icon={icon}
			headerMetadataParts={headerMetadataParts}
			footer={footer}
			title={enrichExcerpt(customTitle || title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={data}
			appMode={appMode}
			hasRelatedLinks={hasRelatedLinks}
			mainContentWidth={mainContentWidth}
		/>
	);
};
